import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import UserGroupsResponse from "interfaces/user-groups-response";
import { getAvailableGroupsPath } from "utils/backend-path-builders";
import { FAILED, LOADING, SUCCEEDED } from "utils/constants";
import { getRequest, isIntegratedApp } from "utils/httpClient";
import { isIntegrationOnlyGroup } from "utils/integration-only-groups";
import { RootState } from "../store";

interface AvailableGroupsState {
  groups: UserGroupsResponse[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | any;
}

export const initialState: AvailableGroupsState = {
  groups: [],
  status: "idle",
  error: null,
};

export const fetchAvailableGroups = createAsyncThunk(
  "availableGroups/fetchOptions",
  async () => {
    if (isIntegratedApp()) {
      return;
    }
    const response = await getRequest(getAvailableGroupsPath());
    return response || [];
  }
);

const slice = createSlice({
  name: "availableGroups",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(fetchAvailableGroups.pending, (state, _) => {
      state.status = LOADING;
    });
    builder.addCase(fetchAvailableGroups.fulfilled, (state, action) => {
      state.status = SUCCEEDED;
      state.groups = action.payload;
    });
    builder.addCase(fetchAvailableGroups.rejected, (state, action) => {
      state.status = FAILED;
      state.error = action.error.message;
    });
  },
});

// Integration-only groups (e.g. Soboba) should never be selectable from the
// regular Sparket UI — their contests require an integrated-app session.
// Memoized so the filtered array keeps the same reference across renders;
// without memoization, useSelector would re-render on every dispatch.
export const getAvailableGroups = createSelector(
  [(state: RootState) => state.availableGroups.groups],
  (groups) => groups.filter(g => !isIntegrationOnlyGroup(g.uri))
);

export const getAvailableGroupsStatus = (state: RootState) => {
  return state.availableGroups.status;
};

export default slice.reducer;
